import DeleteIcon from '@mui/icons-material/Delete';
import { IconButton } from '@mui/material';
import { GridCellParams, GridEditInputCell, GridRenderCellParams, GridRenderEditCellParams } from '@mui/x-data-grid';
import React, { useMemo } from 'react';

import { IColumnMapping, IRow, TInvalidRows } from '../../../../types/addressBook';
import { TableTooltip } from '../../../_mui/_components_/TableTooltip';
import { CellWithError, LabelEditCell } from '../components';

interface IProps {
  mappedColumns: IColumnMapping[];
  invalidRows: TInvalidRows;
  onDeleteRow: (id: string) => void;
  columnWidths: Record<string, number>;
  isCellEditable?: (row: IRow, field: string) => boolean;
}

export const useRepairTableColumns = ({
  mappedColumns,
  invalidRows,
  onDeleteRow,
  columnWidths,
  isCellEditable,
}: IProps) => {
  return useMemo(() => {
    const deleteColumn = {
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: columnWidths.actions,
      minWidth: columnWidths.actions,
      padding: 0,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      editable: false,
      renderCell: (params: GridCellParams) => (
        <TableTooltip title="Delete row">
          <IconButton
            size="small"
            color="error"
            disableRipple
            onClick={(e) => {
              e.stopPropagation();
              onDeleteRow(String(params.id));
            }}
          >
            <DeleteIcon fontSize="small" color="action" sx={{ top: 0 }} />
          </IconButton>
        </TableTooltip>
      ),
    };

    const dataColumns = mappedColumns.map((column, index) => {
      const isFirstColumn = index === 0;

      return {
        field: column.id,
        headerName: column.title,
        minWidth: columnWidths[column.id] || 200,
        editable: true,
        sortable: false,
        renderCell: (params: GridRenderCellParams) => {
          const rowId = String(params.id);
          const displayValue = String(params.value ?? '');
          const errorMessage = invalidRows[rowId]?.[params.field];
          const disabled = isCellEditable ? !isCellEditable(params.row as IRow, params.field) : false;

          if (disabled) {
            return <CellWithError value={displayValue} disabled />;
          }

          if (isFirstColumn) {
            const hasErrors = invalidRows[rowId] && Object.keys(invalidRows[rowId]).length > 0;
            return (
              <CellWithError value={displayValue} errorMessage={errorMessage} showStatus hasRowErrors={hasErrors} />
            );
          }

          return <CellWithError value={displayValue} errorMessage={errorMessage} />;
        },
        renderEditCell: isFirstColumn
          ? (params: GridRenderEditCellParams) => <LabelEditCell params={params} invalidRows={invalidRows} />
          : (params: GridRenderEditCellParams) => <GridEditInputCell {...params} />,
      };
    });

    return [...dataColumns, deleteColumn];
  }, [mappedColumns, invalidRows, onDeleteRow, columnWidths, isCellEditable]);
};
